/* External */
import { OrderedMap, Record } from 'immutable';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

/* Storage */
import { selectAllChannels } from '../storage/channels';

/* Utils */
import { EPG_FILTERS_TYPES, EPG_FILTERS_ID } from '../utils/constants';

export const Filter = Record({
  id: '',
  name: '',
  type: '',
  emptyMessages: {},
});

export default function useEpgFilters() {
  const { formatMessage } = useIntl();
  const defaultEmptyMessage = useMemo(() => ({
    title: formatMessage({ id: 'epgEmptyStations.category.title' }),
    description: formatMessage({ id: 'epgEmptyStations.category.description' }),
  }), [formatMessage]);

  const allStations = useMemo(() => Filter({
    id: EPG_FILTERS_ID.ALL_STATIONS,
    name: formatMessage({ id: 'epgFilters.all' }),
    type: EPG_FILTERS_TYPES.ALL,
    emptyMessages: defaultEmptyMessage,
  }), [formatMessage, defaultEmptyMessage]);

  const mostWatched = useMemo(() => Filter({
    id: EPG_FILTERS_ID.MOST_WATCHED,
    name: formatMessage({ id: 'epgFilters.mostWatched' }),
    type: EPG_FILTERS_TYPES.SECTION,
    emptyMessages: {
      title: formatMessage({ id: 'epgEmptyStations.mostWatched.title' }),
      description: formatMessage({ id: 'epgEmptyStations.mostWatched.description' }),
    },
  }), [formatMessage]);

  const recentlyWatched = useMemo(() => Filter({
    id: EPG_FILTERS_ID.RECENTLY_WATCHED,
    name: formatMessage({ id: 'epgFilters.recentlyWatched' }),
    type: EPG_FILTERS_TYPES.SECTION,
    emptyMessages: {
      title: formatMessage({ id: 'epgEmptyStations.recentlyWatched.title' }),
      description: formatMessage({ id: 'epgEmptyStations.recentlyWatched.description' }),
    },
  }), [formatMessage]);

  const forYou = useMemo(() => Filter({
    id: EPG_FILTERS_ID.FOR_YOU,
    name: formatMessage({ id: 'epgFilters.forYou' }),
    type: EPG_FILTERS_TYPES.FOR_YOU,
    emptyMessages: {
      title: formatMessage({ id: 'epgEmptyStations.forYou.title' }),
      description: formatMessage({ id: 'epgEmptyStations.forYou.description' }),
    },
  }), [formatMessage]);

  const channels = useSelector(selectAllChannels);
  const channelCategories = useMemo(() => channels.valueSeq()
    .flatMap((channel) => channel.categories).toSetSeq().toList()
    .sort((categoryA, categoryB) => {
      if (categoryA === categoryB) return 0;
      return categoryA < categoryB ? -1 : 1;
    }), [channels]);

  const filterCategories = useMemo(() => Object.fromEntries(channelCategories?.map((category) => {
    const id = category.toUpperCase().replace(/\s/g, '_');
    return [
      id,
      Filter({
        id,
        name: category,
        type: EPG_FILTERS_TYPES.CATEGORY,
        emptyMessages: defaultEmptyMessage,
      }),
    ];
  })), [channelCategories, defaultEmptyMessage]);

  const filters = useMemo(() => OrderedMap({
    [allStations.id]: allStations,
    [mostWatched.id]: mostWatched,
    [recentlyWatched.id]: recentlyWatched,
    ...filterCategories,
  }), [
    allStations,
    mostWatched,
    recentlyWatched,
    filterCategories,
  ]);

  const filtersMiniEpg = useMemo(() => OrderedMap({
    [forYou.id]: forYou,
    ...filterCategories,
  }), [
    filterCategories,
    forYou,
  ]);

  return { filters, filtersMiniEpg };
}
